import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useCountry, useTranslations } from '~/components/Locale'
import { Price } from '~/components/utils'
import { ProductDocument } from '~/gql'
import type { HTMLElementProps, Product, Variant } from '~/types'
import { BREAKPOINTS, cn, getPrice, isOk, PRODUCT_IDS, shopifyFetch, useWindowSize } from '~/utils'

interface Props {
	variant?: Variant
}

export const Batch: React.FC<HTMLElementProps<'div'>> = ({ className, ...props }) => (
	<div className={cn('text-18 flex h-full flex-col justify-center', className)} {...props} />
)

export const Label: React.FC<HTMLElementProps<'strong'>> = ({ className, ...props }) => (
	<strong
		className={cn('absolute right-10 top-[-4px] rounded-b-lg bg-white px-[.8em] py-[.3em] text-base', className)}
		{...props}
	/>
)

export const Tier: React.FC<HTMLElementProps<'li'>> = ({ className, ...props }) => (
	<li
		className={cn('bg-gray-150 relative h-36 rounded-3xl border-4 border-solid border-transparent', className)}
		{...props}
	/>
)

export const PresaleBatches: React.FC<{ variant: Variant }> = ({ variant }) => (
	<ul className="list grid grid-cols-1 gap-5 text-center md:grid-cols-3">
		<Tier>
			<Label>
				<s>Delivered</s>
			</Label>
			<Batch>
				<strong>
					<s>Batch 1-4</s>
				</strong>
			</Batch>
		</Tier>
		<Tier>
			<Label>Sold out - Shipping now</Label>
			<Batch>
				<strong>Batch 5-7</strong>
			</Batch>
		</Tier>
		<Tier className="border-green-600 bg-white">
			<Label className="bg-green-600 text-white">Selling now</Label>
			<Batch>
				<strong>
					<Price price={getPrice(variant.compareAtPrice) - getPrice(variant.price)} decimals={0} />
					<span>&nbsp;off</span>
				</strong>
				<span className="my-0 text-base">Shipping early March</span>
			</Batch>
		</Tier>
	</ul>
)

const Timeline: React.FC = () => {
	const centerY = 51
	const textYs = [10, 27, 82, 101.6]
	const fontSizes = [12, 16, 14].map(n => n * 0.85)
	const colors = ['#C4C4C4', '#888', '#353535']
	const screen = useWindowSize()
	const t = useTranslations('react.presale')
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			xmlSpace="preserve"
			fill="none"
			viewBox={`0 0 ${screen.width > BREAKPOINTS.sm ? 650 : 335} 110`}
		>
			<g
				style={{ whiteSpace: 'pre', textAlign: 'center' }}
				fontFamily="Source Sans Pro"
				fontWeight="600"
				letterSpacing="-0.02em"
			>
				<g stroke="#000" strokeWidth={1.5} transform={`translate(0, ${centerY})`}>
					<line x1="0" x2="99.4%" />
					<line x1="98.5%" x2="99.5%" y1="-6" strokeLinecap="round" />
					<line x1="98.5%" x2="99.5%" y1="6" strokeLinecap="round" />
				</g>
				<svg x="16%" fill={colors[0]} textAnchor="middle" style={{ overflow: 'visible' }}>
					<text y={textYs[0]} fontSize={fontSizes[0]} fontWeight={700}>
						{t?.sold_out}
					</text>
					<text y={textYs[1]} fontSize={fontSizes[1]}>
						{t?.batch_1?.replace('{ROUND}', '1')}
					</text>
					<text y={textYs[2]} fontSize={fontSizes[2]}>
						{t?.delivery_date_1}
					</text>
					<circle cy={centerY} r="11.5" fill="#fff" strokeWidth="2" stroke={colors[0]} />
				</svg>
				<svg x="50%" fill={colors[1]} textAnchor="middle" style={{ overflow: 'visible' }}>
					<text y={textYs[0]} fontSize={fontSizes[0]} fontWeight={700}>
						{t?.sold_out}
					</text>
					<text y={textYs[1]} fontSize={fontSizes[1]}>
						{t?.batch_2?.replace('{ROUND}', '2')}
					</text>
					<text y={textYs[2]} fontSize={fontSizes[2]}>
						{t?.delivery_date_2}
					</text>
					<circle cy={centerY} r="11.5" fill="#fff" strokeWidth="2" stroke="#C4C4C4" />
				</svg>
				<svg x="83%" fill={colors[2]} textAnchor="middle" style={{ overflow: 'visible' }}>
					<text y={textYs[0]} fill="#226637" fontSize={fontSizes[0]} fontWeight={700}>
						{t?.selling_fast}
					</text>
					<text y={textYs[1]} fontSize={fontSizes[1]}>
						{t?.batch_3?.replace('{ROUND}', '3')}
					</text>
					<text y={textYs[2]} fontSize={fontSizes[2]}>
						{t?.delivery_date_3}
					</text>
					<circle cy={centerY} r="13.5" fill="none" />
					<circle cy={centerY} r="11.5" fill="#fff" strokeWidth="2" stroke="#52C083" />
					<circle cy={centerY} r="8.5" fill="#52C083" />
				</svg>
			</g>
		</svg>
	)
}

export const PresaleTiers: React.FC<Props> = props => {
	const res = useQuery(['products'], async () =>
		props.variant
			? { data: { variants: [props.variant] } as Product }
			: shopifyFetch(ProductDocument, { id: PRODUCT_IDS.Auk })
	).data
	const variant = isOk(res) ? res?.data?.variants?.[0] : props.variant
	const country = useCountry()
	const t = useTranslations('react.presale', {
		COUNTRY: country.name,
		DISCOUNT: getPrice(getPrice(variant?.compareAtPrice) - getPrice(variant?.price), true)
	})
	return (
		<div className="mx-auto my-24 max-w-screen-lg px-8 text-gray-900 lg:mb-32">
			<h2 className="text-product mb-5 mt-0 leading-none">{t?.limited_offer}</h2>
			<p className="mb-8 text-xl md:text-2xl">{t?.is_coming}</p>
			<Timeline />
		</div>
	)
}
